import { toSentenceCase } from './textTransformations';

/**
 * Parse markdown content into sections based on h2 headings
 * Common utility used across all dynamic split view components
 */
export const parseMarkdownIntoSections = (content) => {
  if (!content) return [];
  
  // Split content by h2 headings (##)
  const sections = [];
  const lines = content.split('\n');
  let currentSection = null;
  let currentContent = [];
  let currentCategory = null;
  let inTableOfContents = false;
  
  lines.forEach((line) => {
    // Check if line is an h1 heading (category)
    const h1Match = line.match(/^#\s+(.+)$/);
    // Check if line is an h2 heading
    const h2Match = line.match(/^##\s+(.+)$/);
    
    if (h1Match && !h2Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          category: currentCategory
        });
      }
      
      // Set new category
      currentCategory = h1Match[1].trim();
      currentSection = null;
      currentContent = [];
    } else if (h2Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          category: currentCategory,
          isFirstInCategory: false
        });
      }
      
      // Start new section
      currentSection = h2Match[1].trim();
      // Remove leading numbers and dots from section titles
      currentSection = currentSection.replace(/^\d+\.\s*/, '');
      
      // Check if this is a table of contents section
      inTableOfContents = currentSection.toLowerCase().includes('table of contents') || 
                         currentSection.toLowerCase().includes('contents') ||
                         currentSection.toLowerCase() === 'toc';
      
      // Convert to sentence case
      currentSection = toSentenceCase(currentSection);
      currentContent = [];
    } else {
      // Add line to current content only if not in table of contents
      if (!inTableOfContents && currentSection) {
        currentContent.push(line);
      }
    }
  });
  
  // Add the last section if not table of contents
  if (currentSection && !inTableOfContents) {
    sections.push({
      id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      title: currentSection,
      content: currentContent.join('\n').trim(),
      category: currentCategory
    });
  }
  
  // Renumber sub-headings in content to match section display index
  sections.forEach((section, index) => {
    if (section.content) {
      // Replace sub-heading numbers like ### X.Y or ### X.Y.Z with ### (index+1).Y or (index+1).Y.Z
      section.content = section.content.replace(
        /^(#{3,}\s+)\d+((?:\.\d+)+)/gm,
        `$1${index + 1}$2`
      );
    }
  });

  // Mark first feature in each category
  let lastCategory = null;
  sections.forEach(section => {
    if (section.category !== lastCategory) {
      section.isFirstInCategory = true;
      lastCategory = section.category;
    }
  });

  return sections;
};

/**
 * Parse security guidelines markdown where:
 * - H2 (##) = Category headers (e.g., "## Security Principles")
 * - H3 (###) = Section accordions (e.g., "### 1. Defense in Depth")
 */
export const parseSecurityGuidelines = (content) => {
  if (!content) return [];

  const sections = [];
  const lines = content.split('\n');
  let currentSection = null;
  let currentContent = [];
  let currentCategory = null;
  let inTableOfContents = false;
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const line = lines[lineIndex];

    // Check if line is an h2 heading (category)
    const h2Match = line.match(/^##\s+(.+)$/);
    // Check if line is an h3 heading (section)
    const h3Match = line.match(/^###\s+(.+)$/);

    if (h2Match && !h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          level: 3,
          isCategory: false
        });
      }

      // Set new category
      const categoryTitle = h2Match[1].trim();
      currentCategory = categoryTitle;

      // Check if this is a table of contents
      inTableOfContents = categoryTitle.toLowerCase().includes('table of contents') ||
                         categoryTitle.toLowerCase().includes('contents') ||
                         categoryTitle.toLowerCase() === 'toc';

      // Collect content until next h2 or h3
      const categoryContent = [];
      lineIndex++;
      while (lineIndex < lines.length) {
        const nextLine = lines[lineIndex];
        const nextH2 = nextLine.match(/^##\s+(.+)$/);
        const nextH3 = nextLine.match(/^###\s+(.+)$/);

        if (nextH2 || nextH3) {
          break;
        }
        categoryContent.push(nextLine);
        lineIndex++;
      }

      // Add category as a section with isCategory flag
      if (!inTableOfContents) {
        sections.push({
          id: `category-${categoryTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
          title: categoryTitle,
          content: categoryContent.join('\n').trim(),
          rawContent: categoryContent.join('\n').trim(),
          level: 2,
          isCategory: true
        });
      }

      currentSection = null;
      currentContent = [];
      continue;
    } else if (h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          level: 3,
          isCategory: false
        });
      }

      // Start new section
      let sectionTitle = h3Match[1].trim();

      // Convert to sentence case but keep the numbering
      currentSection = sectionTitle;
      currentContent = [];
    } else {
      // Add line to current content only if not in table of contents
      if (!inTableOfContents && currentSection) {
        currentContent.push(line);
      }
    }

    lineIndex++;
  }

  // Add the last section if not table of contents
  if (currentSection && !inTableOfContents) {
    sections.push({
      id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      title: currentSection,
      content: currentContent.join('\n').trim(),
      rawContent: currentContent.join('\n').trim(),
      level: 3,
      isCategory: false
    });
  }

  return sections;
};

/**
 * Parse security testing markdown where:
 * - H2 (##) = Category headers (e.g., "## 1. Security Testing Strategy")
 * - H3 (###) = Section accordions (e.g., "### 1.1 Security Testing Objectives")
 */
export const parseSecurityTestingSections = (content) => {
  if (!content) return [];

  const sections = [];
  const lines = content.split('\n');
  let currentSection = null;
  let currentContent = [];
  let currentCategory = null;
  let inTableOfContents = false;
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const line = lines[lineIndex];

    // Check if line is an h2 heading (category)
    const h2Match = line.match(/^##\s+(.+)$/);
    // Check if line is an h3 heading (section)
    const h3Match = line.match(/^###\s+(.+)$/);

    if (h2Match && !h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory,
          isFirstInCategory: false
        });
      }

      // Set new category
      const categoryTitle = h2Match[1].trim();
      currentCategory = categoryTitle;

      // Check if this is a table of contents
      inTableOfContents = categoryTitle.toLowerCase().includes('table of contents') ||
                         categoryTitle.toLowerCase().includes('contents') ||
                         categoryTitle.toLowerCase() === 'toc';

      currentSection = null;
      currentContent = [];
      lineIndex++;
      continue;
    } else if (h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory,
          isFirstInCategory: false
        });
      }

      // Start new section - remove leading numbers and dots
      let sectionTitle = h3Match[1].trim();
      sectionTitle = sectionTitle.replace(/^\d+(\.\d+)*\.?\s*/, '');
      currentSection = sectionTitle;
      currentContent = [];
    } else {
      // Add line to current content only if not in table of contents
      if (!inTableOfContents && currentSection) {
        currentContent.push(line);
      }
    }

    lineIndex++;
  }

  // Add the last section if not table of contents
  if (currentSection && !inTableOfContents) {
    sections.push({
      id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      title: currentSection,
      content: currentContent.join('\n').trim(),
      rawContent: currentContent.join('\n').trim(),
      category: currentCategory,
      isFirstInCategory: false
    });
  }

  // Mark first section in each category
  let lastCategory = null;
  sections.forEach(section => {
    if (section.category !== lastCategory) {
      section.isFirstInCategory = true;
      lastCategory = section.category;
    }
  });

  return sections;
};

/**
 * Parse development guidelines markdown where:
 * - H2 (##) = Category headers (e.g., "Code organization and structure")
 * - H3 (###) = Section accordions (e.g., "1.1 Frontend Project Structure")
 */
export const parseDevelopmentGuidelines = (content) => {
  if (!content) return [];

  const sections = [];
  const lines = content.split('\n');
  let currentSection = null;
  let currentContent = [];
  let currentCategory = null;
  let inTableOfContents = false;

  // First pass: check structure
  const hasH3Sections = content.includes('\n###') || content.startsWith('###');
  const hasH2Categories = /\n##\s+[^#]/.test(content) || /^##\s+[^#]/.test(content);

  // If H3 sections exist without H2 categories, we need auto-categorization
  const needsAutoCategories = hasH3Sections && !hasH2Categories;

  lines.forEach((line) => {
    // Check if line is an h2 heading (category)
    const h2Match = line.match(/^##\s+(.+)$/);
    // Check if line is an h3 heading (section)
    const h3Match = line.match(/^###\s+(.+)$/);

    if (h2Match && !h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          category: currentCategory
        });
      }

      const h2Title = h2Match[1].trim();

      // If no H3 sections exist (flat structure), treat H2 as sections with auto-generated categories
      if (!hasH3Sections) {
        // This is a flat structure - treat H2 as a section
        currentSection = h2Title.replace(/^\d+(\.\d+)*\.?\s*/, ''); // Remove numbering
        currentSection = toSentenceCase(currentSection);
        currentContent = [];

        // Auto-assign category based on section content/position
        // Group every 5-10 sections into a category
        const sectionCount = sections.length;
        if (sectionCount < 10) {
          currentCategory = 'API Design Principles';
        } else if (sectionCount < 20) {
          currentCategory = 'Request & Response Handling';
        } else if (sectionCount < 30) {
          currentCategory = 'Authentication & Security';
        } else if (sectionCount < 40) {
          currentCategory = 'Error Handling & Validation';
        } else if (sectionCount < 50) {
          currentCategory = 'Documentation & Testing';
        } else {
          currentCategory = 'Advanced Guidelines';
        }

        inTableOfContents = false;
      } else {
        // Proper H2+H3 structure - H2 is a category
        currentCategory = h2Title;
        currentSection = null;
        currentContent = [];

        // Check if this is a table of contents
        inTableOfContents = currentCategory.toLowerCase().includes('table of contents') ||
                           currentCategory.toLowerCase().includes('contents') ||
                           currentCategory.toLowerCase() === 'toc';
      }
    } else if (h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          category: currentCategory,
          isFirstInCategory: false
        });
      }

      // Start new section
      currentSection = h3Match[1].trim();
      // Remove leading numbers and dots from section titles (e.g., "1.1" or "1.")
      currentSection = currentSection.replace(/^\d+(\.\d+)*\.?\s*/, '');

      // Convert to sentence case
      currentSection = toSentenceCase(currentSection);
      currentContent = [];

      // Auto-assign category if H3 sections exist without H2 categories
      if (needsAutoCategories) {
        const sectionCount = sections.length;
        if (sectionCount < 10) {
          currentCategory = 'API Design Principles';
        } else if (sectionCount < 20) {
          currentCategory = 'Request & Response Handling';
        } else if (sectionCount < 30) {
          currentCategory = 'Authentication & Security';
        } else if (sectionCount < 40) {
          currentCategory = 'Error Handling & Validation';
        } else if (sectionCount < 50) {
          currentCategory = 'Documentation & Testing';
        } else {
          currentCategory = 'Advanced Guidelines';
        }
      }
    } else {
      // Add line to current content only if not in table of contents
      if (!inTableOfContents && currentSection) {
        currentContent.push(line);
      }
    }
  });

  // Add the last section if not table of contents
  if (currentSection && !inTableOfContents) {
    sections.push({
      id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      title: currentSection,
      content: currentContent.join('\n').trim(),
      category: currentCategory
    });
  }

  // Mark first section in each category
  let lastCategory = null;
  sections.forEach(section => {
    if (section.category !== lastCategory) {
      section.isFirstInCategory = true;
      lastCategory = section.category;
    }
  });


  return sections;
};

/**
 * Parse functional testing markdown where:
 * - H2 (##) = Category headers (e.g., "## Functional Testing Strategy")
 * - H3 (###) = Section accordions (e.g., "### Login Flow Test")
 */
export const parseFunctionalTestingSections = (content) => {
  if (!content) return [];

  const sections = [];
  const lines = content.split('\n');
  let currentSection = null;
  let currentContent = [];
  let currentCategory = null;
  let inTableOfContents = false;
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const line = lines[lineIndex];

    // Check if line is an h2 heading (category)
    const h2Match = line.match(/^##\s+(.+)$/);
    // Check if line is an h3 heading (section)
    const h3Match = line.match(/^###\s+(.+)$/);

    if (h2Match && !h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory
        });
      }

      // Set new category
      const categoryTitle = h2Match[1].trim();
      currentCategory = categoryTitle;

      // Check if this is a table of contents
      inTableOfContents = categoryTitle.toLowerCase().includes('table of contents') ||
                         categoryTitle.toLowerCase().includes('contents') ||
                         categoryTitle.toLowerCase() === 'toc';

      currentSection = null;
      currentContent = [];
      lineIndex++;
      continue;
    } else if (h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory
        });
      }

      // Start new section
      let sectionTitle = h3Match[1].trim();
      // Remove leading numbers and dots from section titles
      sectionTitle = sectionTitle.replace(/^\d+(\.\d+)*\.?\s*/, '');

      currentSection = sectionTitle;
      currentContent = [];
    } else {
      // Add line to current content only if not in table of contents
      if (!inTableOfContents && currentSection) {
        currentContent.push(line);
      }
    }

    lineIndex++;
  }

  // Add the last section if not table of contents
  if (currentSection && !inTableOfContents) {
    sections.push({
      id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      title: currentSection,
      content: currentContent.join('\n').trim(),
      rawContent: currentContent.join('\n').trim(),
      category: currentCategory
    });
  }

  // Mark first section in each category
  let lastCategory = null;
  sections.forEach(section => {
    if (section.category !== lastCategory) {
      section.isFirstInCategory = true;
      lastCategory = section.category;
    }
  });

  return sections;
};

/**
 * Parse unit testing markdown where:
 * - H2 (##) = Category headers (e.g., "## Testing Strategy Overview")
 * - H3 (###) = Section accordions (e.g., "### Testing Pyramid")
 */
export const parseUnitTestingSections = (content) => {
  if (!content) return [];

  const sections = [];
  const lines = content.split('\n');
  let currentSection = null;
  let currentContent = [];
  let currentCategory = null;
  let inTableOfContents = false;
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const line = lines[lineIndex];

    // Check if line is an h2 heading (category)
    const h2Match = line.match(/^##\s+(.+)$/);
    // Check if line is an h3 heading (section)
    const h3Match = line.match(/^###\s+(.+)$/);

    if (h2Match && !h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory
        });
      }

      // Set new category
      const categoryTitle = h2Match[1].trim();
      currentCategory = categoryTitle;

      // Check if this is a table of contents
      inTableOfContents = categoryTitle.toLowerCase().includes('table of contents') ||
                         categoryTitle.toLowerCase().includes('contents') ||
                         categoryTitle.toLowerCase() === 'toc';

      currentSection = null;
      currentContent = [];
      lineIndex++;
      continue;
    } else if (h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory
        });
      }

      // Start new section
      let sectionTitle = h3Match[1].trim();
      // Remove leading numbers and dots from section titles
      sectionTitle = sectionTitle.replace(/^\d+(\.\d+)*\.?\s*/, '');

      currentSection = sectionTitle;
      currentContent = [];
    } else {
      // Add line to current content only if not in table of contents
      if (!inTableOfContents && currentSection) {
        currentContent.push(line);
      }
    }

    lineIndex++;
  }

  // Add the last section if not table of contents
  if (currentSection && !inTableOfContents) {
    sections.push({
      id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      title: currentSection,
      content: currentContent.join('\n').trim(),
      rawContent: currentContent.join('\n').trim(),
      category: currentCategory
    });
  }

  // Mark first section in each category
  let lastCategory = null;
  sections.forEach(section => {
    if (section.category !== lastCategory) {
      section.isFirstInCategory = true;
      lastCategory = section.category;
    }
  });

  return sections;
};
/**
 * Parse automated testing sections from markdown content
 * Uses H2 (##) for categories and H3 (###) for sections
 * @param {string} content - The markdown content to parse
 * @returns {Array} - Array of parsed automated test sections with categories
 */
export const parseAutomatedTestingSections = (content) => {
  if (!content) return [];

  const sections = [];
  const lines = content.split('\n');
  let lineIndex = 0;
  let currentSection = null;
  let currentContent = [];
  let currentCategory = null;
  let inTableOfContents = false;

  while (lineIndex < lines.length) {
    const line = lines[lineIndex];
    const h2Match = line.match(/^##\s+(.+)$/);
    const h3Match = line.match(/^###\s+(.+)$/);

    // H2 indicates a category (e.g., "## Test Automation Strategy")
    if (h2Match && !h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory
        });
      }

      // Update category
      let categoryTitle = h2Match[1].trim();
      // Remove leading numbers and dots from category titles
      categoryTitle = categoryTitle.replace(/^\d+(\.\d+)*\.?\s*/, '');
      currentCategory = categoryTitle;

      // Check if this is a table of contents
      inTableOfContents = categoryTitle.toLowerCase().includes('table of contents') ||
                         categoryTitle.toLowerCase().includes('contents') ||
                         categoryTitle.toLowerCase() === 'toc';

      currentSection = null;
      currentContent = [];
      lineIndex++;
      continue;
    } else if (h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory
        });
      }

      // Start new section
      let sectionTitle = h3Match[1].trim();
      // Remove leading numbers and dots from section titles
      sectionTitle = sectionTitle.replace(/^\d+(\.\d+)*\.?\s*/, '');

      currentSection = sectionTitle;
      currentContent = [];
    } else {
      // Add line to current content only if not in table of contents
      if (!inTableOfContents && currentSection) {
        currentContent.push(line);
      }
    }

    lineIndex++;
  }

  // Add the last section if not table of contents
  if (currentSection && !inTableOfContents) {
    sections.push({
      id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      title: currentSection,
      content: currentContent.join('\n').trim(),
      rawContent: currentContent.join('\n').trim(),
      category: currentCategory
    });
  }

  // Mark first section in each category
  let lastCategory = null;
  sections.forEach(section => {
    if (section.category !== lastCategory) {
      section.isFirstInCategory = true;
      lastCategory = section.category;
    }
  });

  return sections;
};

/**
 * Parse performance testing markdown where:
 * - H2 (##) = Category headers (e.g., "Load Testing", "Stress Testing")
 * - H3 (###) = Individual test cases/sections
 *
 * Skips table of contents sections
 */
export const parsePerformanceTestingSections = (content) => {
  if (!content) return [];

  const sections = [];
  const lines = content.split('\n');
  let currentSection = null;
  let currentContent = [];
  let currentCategory = null;
  let inTableOfContents = false;
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const line = lines[lineIndex];

    // Check if line is an h2 heading (category)
    const h2Match = line.match(/^##\s+(.+)$/);
    // Check if line is an h3 heading (section)
    const h3Match = line.match(/^###\s+(.+)$/);

    if (h2Match && !h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory,
          isFirstInCategory: false
        });
      }

      // Set new category
      const categoryTitle = h2Match[1].trim();
      currentCategory = categoryTitle;

      // Check if this is a table of contents
      inTableOfContents = categoryTitle.toLowerCase().includes('table of contents') ||
                         categoryTitle.toLowerCase().includes('contents') ||
                         categoryTitle.toLowerCase() === 'toc';

      currentSection = null;
      currentContent = [];
      lineIndex++;
      continue;
    } else if (h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory,
          isFirstInCategory: false
        });
      }

      // Start new section - remove leading numbers and dots
      let sectionTitle = h3Match[1].trim();
      sectionTitle = sectionTitle.replace(/^\d+(\.\d+)*\.?\s*/, '');
      currentSection = sectionTitle;
      currentContent = [];
    } else {
      // Add line to current content only if not in table of contents
      if (!inTableOfContents && currentSection) {
        currentContent.push(line);
      }
    }

    lineIndex++;
  }

  // Add the last section if not table of contents
  if (currentSection && !inTableOfContents) {
    sections.push({
      id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      title: currentSection,
      content: currentContent.join('\n').trim(),
      rawContent: currentContent.join('\n').trim(),
      category: currentCategory,
      isFirstInCategory: false
    });
  }

  // Mark first section in each category for performance testing
  let lastCategoryPerf = null;
  sections.forEach(section => {
    if (section.category !== lastCategoryPerf) {
      section.isFirstInCategory = true;
      lastCategoryPerf = section.category;
    }
  });

  return sections;
};

/**
 * Parse git guidelines markdown where:
 * - H2 (##) = Category headers (e.g., "Branching Strategy", "Commit Conventions")
 * - H3 (###) = Section accordions (e.g., "Feature Branches", "Commit Message Format")
 *
 * Skips table of contents sections
 */
export const parseGitGuidelinesSections = (content) => {
  if (!content) return [];

  const sections = [];
  const lines = content.split('\n');
  let currentSection = null;
  let currentContent = [];
  let currentCategory = null;
  let inTableOfContents = false;
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const line = lines[lineIndex];

    // Check if line is an h2 heading (category)
    const h2Match = line.match(/^##\s+(.+)$/);
    // Check if line is an h3 heading (section)
    const h3Match = line.match(/^###\s+(.+)$/);

    if (h2Match && !h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory,
          isFirstInCategory: false
        });
      }

      // Set new category
      const categoryTitle = h2Match[1].trim();
      currentCategory = categoryTitle;

      // Check if this is a table of contents
      inTableOfContents = categoryTitle.toLowerCase().includes('table of contents') ||
                         categoryTitle.toLowerCase().includes('contents') ||
                         categoryTitle.toLowerCase() === 'toc';

      currentSection = null;
      currentContent = [];
      lineIndex++;
      continue;
    } else if (h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory,
          isFirstInCategory: false
        });
      }

      // Start new section - remove leading numbers and dots
      let sectionTitle = h3Match[1].trim();
      sectionTitle = sectionTitle.replace(/^\d+(\.\d+)*\.?\s*/, '');
      currentSection = sectionTitle;
      currentContent = [];
    } else {
      // Add line to current content only if not in table of contents
      if (!inTableOfContents && currentSection) {
        currentContent.push(line);
      }
    }

    lineIndex++;
  }

  // Add the last section if not table of contents
  if (currentSection && !inTableOfContents) {
    sections.push({
      id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      title: currentSection,
      content: currentContent.join('\n').trim(),
      rawContent: currentContent.join('\n').trim(),
      category: currentCategory,
      isFirstInCategory: false
    });
  }

  // Mark first section in each category for git guidelines
  let lastCategoryGit = null;
  sections.forEach(section => {
    if (section.category !== lastCategoryGit) {
      section.isFirstInCategory = true;
      lastCategoryGit = section.category;
    }
  });

  return sections;
};

/**
 * Parse CI/CD pipeline markdown where:
 * - H2 (##) = Category headers (e.g., "Build Stage", "Test Stage", "Deploy Stage")
 * - H3 (###) = Section accordions (e.g., "Docker Build", "Unit Tests", "Production Deploy")
 *
 * Skips table of contents sections
 */
export const parseCICDPipelineSections = (content) => {
  if (!content) return [];

  const sections = [];
  const lines = content.split('\n');
  let currentSection = null;
  let currentContent = [];
  let currentCategory = null;
  let inTableOfContents = false;
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const line = lines[lineIndex];

    // Check if line is an h2 heading (category)
    const h2Match = line.match(/^##\s+(.+)$/);
    // Check if line is an h3 heading (section)
    const h3Match = line.match(/^###\s+(.+)$/);

    if (h2Match && !h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory,
          isFirstInCategory: false
        });
      }

      // Set new category
      const categoryTitle = h2Match[1].trim();
      currentCategory = categoryTitle;

      // Check if this is a table of contents
      inTableOfContents = categoryTitle.toLowerCase().includes('table of contents') ||
                         categoryTitle.toLowerCase().includes('contents') ||
                         categoryTitle.toLowerCase() === 'toc';

      currentSection = null;
      currentContent = [];
      lineIndex++;
      continue;
    } else if (h3Match) {
      // Save previous section if exists and not table of contents
      if (currentSection && !inTableOfContents) {
        sections.push({
          id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: currentSection,
          content: currentContent.join('\n').trim(),
          rawContent: currentContent.join('\n').trim(),
          category: currentCategory,
          isFirstInCategory: false
        });
      }

      // Start new section - remove leading numbers and dots
      let sectionTitle = h3Match[1].trim();
      sectionTitle = sectionTitle.replace(/^\d+(\.\d+)*\.?\s*/, '');
      currentSection = sectionTitle;
      currentContent = [];
    } else {
      // Add line to current content only if not in table of contents
      if (!inTableOfContents && currentSection) {
        currentContent.push(line);
      }
    }

    lineIndex++;
  }

  // Add the last section if not table of contents
  if (currentSection && !inTableOfContents) {
    sections.push({
      id: currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      title: currentSection,
      content: currentContent.join('\n').trim(),
      rawContent: currentContent.join('\n').trim(),
      category: currentCategory,
      isFirstInCategory: false
    });
  }

  // Mark first section in each category for CI/CD pipeline
  let lastCategoryCICD = null;
  sections.forEach(section => {
    if (section.category !== lastCategoryCICD) {
      section.isFirstInCategory = true;
      lastCategoryCICD = section.category;
    }
  });

  return sections;
};
